'use strict';

// Constants
const VIEWPORT_GAP_PX = 12;
const STICKY_MODES = ['sticky', 'stickyOverlay'];

/**
 * Header height from --header-height (set by stickyHeader.js). Falls back to offsetHeight.
 * @param {HTMLElement} header - pageHeader element.
 * @returns {number} Header height in pixels, or 0 when missing.
 */
function getPageHeaderHeight(header) {
    const raw = (header.style.getPropertyValue('--header-height') || window.getComputedStyle(header).getPropertyValue('--header-height')).trim();
    const parsed = parseFloat(raw);

    if (!Number.isNaN(parsed) && parsed > 0) return parsed;

    return header.offsetHeight;
}

/**
 * Reveals stickyOverlay header before scrolling up (same as user scrolling up).
 * @param {HTMLElement} header - pageHeader element.
 * @returns {void}
 */
function revealOverlayHeader(header) {
    if (header.getAttribute('data-sticky-mode') !== 'stickyOverlay') return;

    header.classList.add('scroll-up');
}

/**
 * Offset to leave above a target when scrolling (sticky header + small gap).
 * @returns {number} Non-negative offset in pixels.
 */
function getFixedHeaderScrollOffset() {
    const header = document.getElementById('pageHeader');

    if (!header) return VIEWPORT_GAP_PX;

    const stickyMode = header.getAttribute('data-sticky-mode') || '';
    const isStickySite = STICKY_MODES.indexOf(stickyMode) !== -1;

    if (!isStickySite || !header.classList.contains('sticky')) return VIEWPORT_GAP_PX;

    const headerHeight = getPageHeaderHeight(header);

    return headerHeight > 0 ? headerHeight + VIEWPORT_GAP_PX : VIEWPORT_GAP_PX;
}

/**
 * Scrolls an element below the page header.
 * @param {HTMLElement} el - Target element.
 * @returns {void}
 */
function scrollElementBelowHeader(el) {
    if (!el) return;

    window.requestAnimationFrame(function () {
        const header = document.getElementById('pageHeader');

        if (header) revealOverlayHeader(header);

        const rect = el.getBoundingClientRect();
        const top = window.scrollY + rect.top - getFixedHeaderScrollOffset();

        window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth'
        });
    });
}

/**
 * Restores a previously saved window scroll position (e.g. after collapsing expanded content).
 * Reveals sticky overlay header when scrolling up and clamps to the current document height.
 * @param {number} scrollTop - Saved window.scrollY value.
 * @returns {void}
 */
function restoreWindowScrollTop(scrollTop) {
    if (typeof scrollTop !== 'number' || Number.isNaN(scrollTop)) return;

    window.requestAnimationFrame(function () {
        const header = document.getElementById('pageHeader');
        const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

        if (header) revealOverlayHeader(header);

        window.scrollTo({
            top: Math.min(Math.max(0, scrollTop), maxScrollTop),
            behavior: 'smooth'
        });
    });
}

module.exports = {
    getFixedHeaderScrollOffset: getFixedHeaderScrollOffset,
    scrollElementBelowHeader: scrollElementBelowHeader,
    restoreWindowScrollTop: restoreWindowScrollTop
};
